// @rpg-unit/standard-settings: the standard setting rows and accessibility defaults (tier 3; composition framework,
// decision 0077). Cut from spike 002's `content/tables/settings.json` and `accessibility.json` on 2026-10-20: the rows
// almost every game has (volume per audio bus, hold or toggle for sprint and block, camera sensitivity, invert and
// shake, the graphics tier, reduce motion, text size and speed, contrast, sound captions, screen flashes, the
// telemetry opt-in) and accessibility's numbers with their sources (the flash cap per WCAG 2.3.1, how long captions
// and subtitles stay). A game adds its own rows beside these (spike 002's `game-settings` table); the settings
// feature (@rpg-unit/settings) builds the model and the screen over both.
//
// A game takes them three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content`) and add
//     `STANDARD_SETTINGS_CONTENT` to the tables it loads in the browser; the tables are `settings` and `accessibility`;
//   - as rows: `standardSettingRows({ omit })` (drop what the game has no use for: `controls.block` in a game with no
//     block), `STANDARD_SETTING_GROUPS` for the screen's sections, in order;
//   - tuned by perceptual knobs: `tuneSettings(rows, { loudness, calm })`.
// Labels are string ids (`setting.audio.master`); `STANDARD_SETTING_STRINGS` holds the English defaults.
import { checkActions } from "@rpg/contracts";
import settingsTable from "./content/tables/settings.json" with { type: "json" };
import settingsSchema from "./content/schemas/settings.schema.json" with { type: "json" };
import accessibilityTable from "./content/tables/accessibility.json" with { type: "json" };
import accessibilitySchema from "./content/schemas/accessibility.schema.json" with { type: "json" };
import strings from "./strings.json" with { type: "json" };

/** This unit's version (the manifest's and package.json's). */
export const STANDARD_SETTINGS_VERSION = "0.1.0" as const;

/** A string reference (`{ table: "strings", id }`). */
export interface StringRef {
  table: string;
  id: string;
}

/** One setting row, as its schema describes it (`src/content/schemas/settings.schema.json`). */
export interface SettingRow {
  id: string;
  group: "audio" | "controls" | "camera" | "display" | "accessibility" | "privacy";
  kind: "range" | "toggle" | "choice";
  label: StringRef;
  default: number | boolean | string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: StringRef }[];
}

/** Accessibility's numbers (one row, `default`), as its schema describes it. */
export interface AccessibilityDefaults {
  id: string;
  /** Flashes allowed to start in any one second, per the `accessibility.flashes` setting. */
  flashCap: { standard: number; reduced: number };
  /** How long a sound caption shows, how many at once, and the window in which a repeat refreshes the one showing. */
  captionSeconds: number;
  captionMax: number;
  captionMerge: number;
  /** The shortest a subtitle stays. */
  subtitleMinSeconds: number;
  /** How many recent things the recap on load lists. */
  recapEvents: number;
  /** Why, with the sources. */
  reason: string;
}

/** The standard rows as a content table. */
export const STANDARD_SETTINGS_TABLE = settingsTable as { table: "settings"; version: number; rows: SettingRow[] };
/** Accessibility's defaults as a content table (one row, `default`). */
export const ACCESSIBILITY_DEFAULTS_TABLE = accessibilityTable as { table: "accessibility"; version: number; rows: AccessibilityDefaults[] };

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const STANDARD_SETTINGS_CONTENT = {
  package: "@rpg-unit/standard-settings",
  version: STANDARD_SETTINGS_VERSION,
  tables: [STANDARD_SETTINGS_TABLE, ACCESSIBILITY_DEFAULTS_TABLE],
  schemas: { settings: settingsSchema as Record<string, unknown>, accessibility: accessibilitySchema as Record<string, unknown> },
} as const;

/** English defaults for every label, option and group heading (`{ id, text }` rows for a strings table). */
export const STANDARD_SETTING_STRINGS = strings as { id: string; text: string }[];

/** The settings screen's sections, in order, with their headings (string ids). */
export const STANDARD_SETTING_GROUPS = [
  { id: "display", label: "settings.group.display" },
  { id: "audio", label: "settings.group.audio" },
  { id: "camera", label: "settings.group.camera" },
  { id: "controls", label: "settings.group.controls" },
  { id: "accessibility", label: "settings.group.accessibility" },
  { id: "privacy", label: "settings.group.privacy" },
] as const;

/** The setting id a hold-or-toggle row uses for an action (`controls.sprint`). */
export const holdSettingId = (action: string): string => `controls.${action}`;

/** The standard rows (copies), less any the game leaves out. */
export function standardSettingRows(o: { omit?: readonly string[] } = {}): SettingRow[] {
  const omit = new Set(o.omit ?? []);
  for (const id of omit) if (!STANDARD_SETTINGS_TABLE.rows.some((r) => r.id === id)) throw new Error(`standard settings: no row "${id}" to omit`);
  return STANDARD_SETTINGS_TABLE.rows.filter((r) => !omit.has(r.id)).map((r) => structuredClone(r));
}

/** Accessibility's defaults (a copy of the `default` row). */
export function accessibilityDefaults(): AccessibilityDefaults {
  return structuredClone(ACCESSIBILITY_DEFAULTS_TABLE.rows.find((r) => r.id === "default")!);
}

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * A setting row's rule beyond its schema (an @rpg/content `RowCheck`: null when fine, else the problem): each kind has
 * what it needs and the default fits it (a range's inside min..max, a choice's among its options).
 */
export function checkSettingRow(r: Record<string, unknown>): string | null {
  if (r.kind === "range") {
    if (!num(r.min) || !num(r.max)) return "a range needs min and max";
    if (!num(r.default) || r.default < r.min || r.default > r.max) return `default ${String(r.default)} is outside ${r.min}..${r.max}`;
  } else if (r.kind === "toggle") {
    if (typeof r.default !== "boolean") return "a toggle's default is true or false";
  } else if (r.kind === "choice") {
    const values = Array.isArray(r.options) ? r.options.map((o) => (o as { value?: unknown }).value) : [];
    if (values.length < 2) return "a choice needs at least two options";
    if (!values.includes(r.default)) return `default ${String(r.default)} is not one of its options`;
  }
  return null;
}

/**
 * The hold-or-toggle rows' problems: each `controls.<action>` row names a standard action (@rpg/contracts) that is a
 * button, and offers exactly hold and toggle. Empty when fine.
 */
export function checkHoldRows(rows: readonly SettingRow[]): string[] {
  const out: string[] = [];
  const holds = rows.filter((r) => r.id.startsWith("controls."));
  const actions = holds.map((r) => ({ id: r.id.slice("controls.".length), kind: "button" as const }));
  for (const i of checkActions(actions)) out.push(`${i.id}: ${i.message}`);
  for (const r of holds) {
    const values = (r.options ?? []).map((o) => o.value).sort();
    if (r.kind !== "choice" || values.join(",") !== "hold,toggle") out.push(`${r.id}: a hold-or-toggle row is a choice of hold and toggle`);
  }
  return out;
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const STANDARD_SETTING_KNOBS = {
  loudness: "louder or quieter out of the box: scales every volume's default (clamped to its range, snapped to its step). 1 is as tuned.",
  calm: "calmer or livelier by default, for a game whose players want comfort first: 0 is as tuned; above 0 the camera shake's default drops by that share; at 0.5 or more screen flashes default to reduced; at 1 reduce motion defaults to on.",
} as const;

/** Knob values. */
export interface SettingKnobs {
  /** Volume defaults × this (default 1). */
  loudness?: number;
  /** 0 (as tuned) to 1 (calmest). */
  calm?: number;
}

const snap = (r: SettingRow, v: number): number => {
  const c = Math.min(r.max!, Math.max(r.min!, v));
  const s = r.step ? r.min! + Math.round((c - r.min!) / r.step) * r.step : c;
  return Number(Math.min(r.max!, s).toFixed(6));
};

/** The rows turned by the knobs (new rows; the input is untouched). With no knobs, equal to the input. */
export function tuneSettings(rows: readonly SettingRow[], knobs: SettingKnobs = {}): SettingRow[] {
  const loudness = knobs.loudness ?? 1;
  const calm = knobs.calm ?? 0;
  if (!(loudness >= 0) || !(calm >= 0 && calm <= 1)) throw new Error(`standard settings: loudness must be ≥ 0 and calm in 0..1 (got ${loudness}, ${calm})`);
  return rows.map((r) => {
    const out = structuredClone(r);
    if (loudness !== 1 && out.group === "audio" && out.kind === "range") out.default = snap(out, (out.default as number) * loudness);
    if (calm > 0) {
      if (out.id === "camera.shake") out.default = snap(out, (out.default as number) * (1 - calm));
      if (out.id === "accessibility.flashes" && calm >= 0.5) out.default = "reduced";
      if (out.id === "accessibility.reduceMotion" && calm >= 1) out.default = "on";
    }
    return out;
  });
}
